import moment from "moment-timezone";

export const TIMEZONE_IDENTIFIERS = [
  "UTC",
  // UTC-10
  "Pacific/Honolulu",
  // UTC-8
  "America/Anchorage",
  "America/Juneau",
  // UTC-7
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Vancouver",
  // UTC-6
  "America/Denver",
  "America/Mexico_City",
  "America/El_Salvador",
  // UTC-5
  "America/Bogota",
  "America/Chicago",
  "America/Lima",
  // UTC-4
  "America/Caracas",
  "America/New_York",
  "America/Santiago",
  "America/Toronto",
  // UTC-3
  "America/Argentina/Buenos_Aires",
  "America/Halifax",
  "America/Sao_Paulo",
  // UTC
  "Atlantic/Azores",
  "Atlantic/Reykjavik",
  // UTC+1
  "Africa/Casablanca",
  "Europe/Dublin",
  "Africa/Lagos",
  "Europe/Lisbon",
  "Europe/London",
  "Africa/Tunis",
  // UTC+2
  "Europe/Amsterdam",
  "Europe/Belgrade",
  "Europe/Berlin",
  "Europe/Bratislava",
  "Europe/Brussels",
  "Europe/Budapest",
  "Europe/Copenhagen",
  "Africa/Johannesburg",
  "Europe/Ljubljana",
  "Europe/Luxembourg",
  "Europe/Madrid",
  "Europe/Malta",
  "Europe/Oslo",
  "Europe/Paris",
  "Europe/Prague",
  "Europe/Rome",
  "Europe/Stockholm",
  "Europe/Vienna",
  "Europe/Warsaw",
  "Europe/Zagreb",
  "Europe/Zurich",
  // UTC+3
  "Europe/Athens",
  "Asia/Bahrain",
  "Europe/Bucharest",
  "Africa/Cairo",
  "Europe/Helsinki",
  "Europe/Istanbul",
  "Asia/Jerusalem",
  "Asia/Kuwait",
  "Europe/Moscow",
  "Africa/Nairobi",
  "Europe/Nicosia",
  "Asia/Qatar",
  "Europe/Riga",
  "Asia/Riyadh",
  "Europe/Sofia",
  "Europe/Tallinn",
  "Europe/Vilnius",
  // UTC+3:30
  "Asia/Tehran",
  // UTC+4
  "Asia/Dubai",
  "Asia/Muscat",
  // UTC+4:30
  "Asia/Kabul",
  // UTC+5
  "Asia/Ashgabat",
  "Asia/Almaty",
  "Asia/Karachi",
  // UTC+5:30
  "Asia/Colombo",
  "Asia/Kolkata",
  // UTC+5:45
  "Asia/Kathmandu",
  // UTC+6
  "Asia/Dhaka",
  // UTC+6:30
  "Asia/Yangon",
  // UTC+7
  "Asia/Bangkok",
  "Asia/Ho_Chi_Minh",
  "Asia/Jakarta",
  // UTC+8
  "Asia/Chongqing",
  "Asia/Hong_Kong",
  "Asia/Kuala_Lumpur",
  "Asia/Manila",
  "Australia/Perth",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Taipei",
  // UTC+9
  "Asia/Seoul",
  "Asia/Tokyo",
  // UTC+9:30
  "Australia/Adelaide",
  // UTC+10
  "Australia/Brisbane",
  "Australia/Sydney",
  // UTC+11
  "Pacific/Norfolk",
  // UTC+12
  "Pacific/Auckland",
  // UTC+12:45
  "Pacific/Chatham",
  // UTC+13
  "Pacific/Apia",
];

export function detectLocalTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export const getGmtOffset = (tz) => {
  if (tz === "UTC") return "+00:00";
  try {
    const now = moment();
    const offset = now.tz(tz).utcOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset >= 0 ? "+" : "-";
    return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  } catch {
    return "";
  }
};

export const getCityName = (tz) => {
  if (tz === "UTC") return "UTC";
  const parts = tz.split("/");
  const city = parts[parts.length - 1].replace(/_/g, " ");
  return city;
};

export const buildTimezoneList = () => {
  const localTz = detectLocalTimezone();
  const localOffset = getGmtOffset(localTz);
  const localCity = getCityName(localTz);
  const localLabel = localOffset
    ? `Local Time (GMT${localOffset}) ${localCity}`
    : `Local Time ${localCity}`;
  const list = [{ label: localLabel, value: "local" }];
  TIMEZONE_IDENTIFIERS.forEach((tz) => {
    const offset = getGmtOffset(tz);
    const city = getCityName(tz);
    const label = offset ? `(GMT${offset}) ${city}` : city;
    list.push({ label, value: tz });
  });
  return list;
};

export const TIMEZONES = buildTimezoneList();

export const buildGmtOffsetList = () => {
  const list = [{ label: "UTC", value: "UTC" }];
  for (let totalMinutes = -720; totalMinutes <= 840; totalMinutes += 30) {
    const sign = totalMinutes >= 0 ? "+" : "-";
    const absMinutes = Math.abs(totalMinutes);
    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;
    const value = `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    list.push({ label: `GMT${value}`, value });
  }
  return list;
};

export const GMT_OFFSETS = buildGmtOffsetList();

const parseGmtOffsetMinutes = (offset) => {
  if (!offset || offset === "UTC") return 0;
  const sign = offset.startsWith("-") ? -1 : 1;
  const [hours, minutes] = offset.slice(1).split(":").map(Number);
  return sign * (hours * 60 + minutes);
};

const getActiveServerOffset = (account, now = new Date()) => {
  const {
    serverTimeWinterOffset,
    serverTimeSummerOffset,
    serverTimeDstStart,
    serverTimeDstEnd,
  } = account || {};

  if (!serverTimeWinterOffset && !serverTimeSummerOffset) return "UTC";

  const monthDay = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  let isSummer = false;
  if (serverTimeDstStart && serverTimeDstEnd && serverTimeSummerOffset) {
    if (serverTimeDstStart <= serverTimeDstEnd) {
      isSummer = monthDay >= serverTimeDstStart && monthDay <= serverTimeDstEnd;
    } else {
      isSummer = monthDay >= serverTimeDstStart || monthDay <= serverTimeDstEnd;
    }
  }

  if (isSummer && serverTimeSummerOffset) return serverTimeSummerOffset;
  if (serverTimeWinterOffset) return serverTimeWinterOffset;
  return serverTimeSummerOffset || "UTC";
};

/**
 * Get the currently active server time for an account as a parts object,
 * taking winter/summer offsets and DST switch dates into account.
 */
export const getCurrentServerTimeParts = (account, now = new Date()) => {
  if (!account?.serverTimeWinterOffset && !account?.serverTimeSummerOffset) return null;

  const activeOffset = getActiveServerOffset(account, now);
  const offsetMinutes = parseGmtOffsetMinutes(activeOffset);
  const serverDate = new Date(now.getTime() + offsetMinutes * 60000);
  const time = `${String(serverDate.getUTCHours()).padStart(2, "0")}:${String(serverDate.getUTCMinutes()).padStart(2, "0")}`;
  const label = activeOffset === "UTC" ? "UTC" : `GMT${activeOffset}`;

  return {
    time,
    label,
    season: activeOffset === account.serverTimeSummerOffset ? "summer" : "winter",
  };
};

/**
 * Convert a trade date/time (in the original trade timezone) into the
 * account's broker server time.
 */
/**
 * Return a Date object that, when passed to a browser-local DateTimePicker,
 * displays the trade's entry/exit time in the account's server time.
 */
export const getServerTimePickerValue = (dateStr, timeStr, originalTimezone, account) => {
  if (!dateStr || !account) return null;

  try {
    const dateTimeStr = timeStr ? `${dateStr} ${timeStr}` : dateStr;
    const originalMoment = moment.tz(
      dateTimeStr,
      "DD/MM/YY HH:mm",
      originalTimezone || "UTC",
    );

    if (!originalMoment.isValid()) return null;

    // Estimate the offset based on the original time, then shift to the
    // server wall date and recompute so DST/day-wrap is handled.
    const estimateOffset = getActiveServerOffset(account, originalMoment.toDate());
    const estimateMinutes = parseGmtOffsetMinutes(estimateOffset);
    const serverWallEstimate = originalMoment.valueOf() + estimateMinutes * 60000;
    const browserOffset = new Date(serverWallEstimate).getTimezoneOffset();
    const wallDate = new Date(serverWallEstimate + browserOffset * 60000);
    const activeOffset = getActiveServerOffset(account, wallDate);
    const offsetMinutes = parseGmtOffsetMinutes(activeOffset);
    const serverWallTimestamp = originalMoment.valueOf() + offsetMinutes * 60000;
    const finalBrowserOffset = new Date(serverWallTimestamp).getTimezoneOffset();
    return new Date(serverWallTimestamp + finalBrowserOffset * 60000);
  } catch (error) {
    console.error("Error building server time picker value:", error);
    return null;
  }
};

/**
 * Convert a DateTimePicker value (where the picker display equals server wall
 * time) back into the trade's original timezone date/time strings.
 */
export const convertServerTimePickerDate = (pickerDate, originalTimezone, account) => {
  if (!pickerDate || !account) return { date: "", time: "" };

  try {
    // pickerDate is displayed in browser local time, and we built it so the
    // local wall time equals the account's server wall time.
    const browserOffset = pickerDate.getTimezoneOffset();
    const activeOffset = getActiveServerOffset(account, pickerDate);
    const offsetMinutes = parseGmtOffsetMinutes(activeOffset);
    const serverWallTimestamp = pickerDate.getTime() - browserOffset * 60000;
    const originalTimestamp = serverWallTimestamp - offsetMinutes * 60000;
    const originalMoment = moment.utc(originalTimestamp).tz(originalTimezone || "UTC");

    return {
      date: originalMoment.format("DD/MM/YY"),
      time: originalMoment.format("HH:mm"),
    };
  } catch (error) {
    console.error("Error converting server time picker date:", error);
    return { date: "", time: "" };
  }
};

export const formatDateTimeInServerTime = (dateStr, timeStr, originalTimezone, account) => {
  if (!dateStr || !account) return "";

  try {
    const dateTimeStr = timeStr ? `${dateStr} ${timeStr}` : dateStr;
    const originalMoment = moment.tz(
      dateTimeStr,
      "DD/MM/YY HH:mm",
      originalTimezone || "UTC",
    );

    if (!originalMoment.isValid()) return "";

    // Estimate, then shift to server wall and recompute to handle DST/day wrap.
    const estimateOffset = getActiveServerOffset(account, originalMoment.toDate());
    const estimateMinutes = parseGmtOffsetMinutes(estimateOffset);
    const serverWallEstimate = originalMoment.valueOf() + estimateMinutes * 60000;
    const browserOffset = new Date(serverWallEstimate).getTimezoneOffset();
    const wallDate = new Date(serverWallEstimate + browserOffset * 60000);
    const activeOffset = getActiveServerOffset(account, wallDate);
    const offsetMinutes = parseGmtOffsetMinutes(activeOffset);
    const serverDate = new Date(originalMoment.valueOf() + offsetMinutes * 60000);
    const format = timeStr ? "DD/MM/YY HH:mm" : "DD/MM/YY";
    return moment.utc(serverDate).format(format);
  } catch (error) {
    console.error("Error formatting server time:", error);
    return "";
  }
};

export const getCurrentServerTimeLabel = (account) => {
  const parts = getCurrentServerTimeParts(account);
  if (!parts) return "Not set";
  return `${parts.label} (${parts.season})`;
};
